/**
 * Client for live payload classification at the DDR gateway (Bank II-7,
 * III-44). Wire shapes mirror
 * apps/api-controller/src/modules/detection/payload-classification/.
 * Kept out of api.ts so parallel work on that file does not collide with this.
 */

import { apiFetch } from './api';

export const PAYLOAD_ROUTE_WRITE_PERMISSION = 'detection:rule:approve';

export interface PayloadRoute {
  id: string;
  route: string;
  sampleRate: number;
  maxPayloadBytes: number;
  enabled: boolean;
  updatedByUserId: string;
  updatedAt: string;
}

export interface PayloadRouteInput {
  route: string;
  sampleRate: number;
  maxPayloadBytes: number;
  enabled: boolean;
}

export interface PayloadRouteSummary {
  route: string;
  direction: 'request' | 'response';
  sampled: number;
  classified: number;
  skippedOverCap: number;
  skippedBusy: number;
  bytesClassified: number;
  classifyMicros: number;
  avgClassifyMicros: number | null;
  /** Coverage (statement counters; on the request row, 0 on the response row). */
  seen: number;
  unparsed: number;
  extendedProtocol: number;
  multiStatement: number;
  shared: number;
  /**
   * Per direction: sampled payloads whose data passed (or moved) but was not
   * read: COPY in binary format or with options the gateway cannot read,
   * COPY to or from a server file or program, binary result columns.
   * Optional: a controller of an earlier round does not send it.
   */
  notRead?: number;
  /** `*` row only: table references past the gateway's per-statement limits. */
  tablesTruncated?: number;
  dataClasses: Record<string, number>;
  lastReceivedAt: string;
}

const base = '/v1/detection/payload-classification';

export async function listPayloadRoutes(): Promise<PayloadRoute[]> {
  return (await apiFetch<{ routes: PayloadRoute[] }>(`${base}/routes`)).routes;
}

export async function savePayloadRoute(input: PayloadRouteInput): Promise<PayloadRoute> {
  return apiFetch<PayloadRoute>(`${base}/routes`, { method: 'PUT', body: JSON.stringify(input) });
}

export async function deletePayloadRoute(id: string): Promise<void> {
  await apiFetch<unknown>(`${base}/routes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function getPayloadSummary(): Promise<{ days: number; routes: PayloadRouteSummary[] }> {
  return apiFetch<{ days: number; routes: PayloadRouteSummary[] }>(`${base}/summary`);
}

/** "pan 4, aadhaar 4", largest first; "none found" when empty. */
export function dataClassesLabel(classes: Record<string, number>): string {
  const parts = Object.entries(classes)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([k, n]) => `${k} ${n.toLocaleString('en-IN')}`);
  return parts.length ? parts.join(', ') : 'none found';
}

/** Validate the form before it is sent; null when it is fine. */
export function routeInputError(route: string, ratePercent: string, capKb: string): string | null {
  const r = route.trim().toLowerCase();
  if (!/^(\*|[a-z0-9_$][a-z0-9_$.-]*)$/.test(r) || r.length > 255) {
    return 'Route is a table name (letters, digits, _ $ . -), optionally with its schema, or * for every other route.';
  }
  const rate = Number(ratePercent);
  if (!Number.isFinite(rate) || rate < 0 || rate > 100) return 'Sample rate is a percentage from 0 to 100.';
  const cap = Number(capKb);
  if (!Number.isFinite(cap) || cap <= 0 || cap > 1024) return 'Size cap is from 1 KB to 1024 KB.';
  return null;
}

/**
 * The rule that governs a reported route, as the gateway looks one up: the
 * route's own rule (the route itself, then the table without its schema),
 * then `*`. An OFF own rule still governs (the gateway excludes the table, it
 * does not fall through to `*`). Null when nothing covers it. The `*` route
 * itself is the controller's row of unattributed statements, not a table.
 */
export function ruleForRoute(rules: ReadonlyArray<PayloadRoute>, route: string): PayloadRoute | null {
  if (route === '*') return null;
  const by = new Map(rules.map((r) => [r.route, r] as const));
  const own = by.get(route) ?? (route.includes('.') ? by.get(route.slice(route.lastIndexOf('.') + 1)) : undefined);
  return own ?? by.get('*') ?? null;
}

/** What the gateway reported, over the summary window, for the routes one rule governs. */
export interface RuleCoverage {
  routes: number;
  seen: number;
  sampled: number;
  unparsed: number;
  extendedProtocol: number;
  multiStatement: number;
  shared: number;
  skippedBusy: number;
  skippedOverCap: number;
  notRead: number;
  lastReceivedAt: string | null;
}

/** A sampling rule whose routes have not reported for this long is flagged. */
export const STALE_AFTER_MS = 60 * 60 * 1000;

export function isRuleActive(r: PayloadRoute): boolean {
  return r.enabled && r.sampleRate > 0;
}

/** Coverage per rule id, from the summary rows. */
export function coverageByRule(
  rules: ReadonlyArray<PayloadRoute>,
  summary: ReadonlyArray<PayloadRouteSummary>,
): Map<string, RuleCoverage> {
  const out = new Map<string, RuleCoverage>();
  const routesSeen = new Map<string, Set<string>>();
  for (const r of rules) {
    out.set(r.id, {
      routes: 0,
      seen: 0,
      sampled: 0,
      unparsed: 0,
      extendedProtocol: 0,
      multiStatement: 0,
      shared: 0,
      skippedBusy: 0,
      skippedOverCap: 0,
      notRead: 0,
      lastReceivedAt: null,
    });
    routesSeen.set(r.id, new Set());
  }
  for (const s of summary) {
    const rule = ruleForRoute(rules, s.route);
    if (!rule) continue;
    const c = out.get(rule.id)!;
    routesSeen.get(rule.id)!.add(s.route);
    c.seen += s.seen;
    c.unparsed += s.unparsed;
    c.extendedProtocol += s.extendedProtocol;
    c.multiStatement += s.multiStatement;
    c.shared += s.shared;
    c.skippedOverCap += s.skippedOverCap;
    c.notRead += s.notRead ?? 0;
    if (s.direction === 'request') {
      c.sampled += s.sampled;
      c.skippedBusy += s.skippedBusy;
    }
    if (!c.lastReceivedAt || s.lastReceivedAt > c.lastReceivedAt) c.lastReceivedAt = s.lastReceivedAt;
  }
  for (const [id, set] of routesSeen) out.get(id)!.routes = set.size;
  return out;
}

/** Statements the gateway could not attribute to any table (the `*` request row). */
export function unattributedCount(summary: ReadonlyArray<PayloadRouteSummary>): number {
  return summary.filter((s) => s.route === '*' && s.direction === 'request').reduce((a, s) => a + s.unparsed, 0);
}

/** Table references past the gateway's per-statement limits (the `*` request row). */
export function tablesTruncatedCount(summary: ReadonlyArray<PayloadRouteSummary>): number {
  return summary.filter((s) => s.route === '*' && s.direction === 'request').reduce((a, s) => a + (s.tablesTruncated ?? 0), 0);
}

/** Whether a rule's coverage is stale: it samples, and nothing reported within STALE_AFTER_MS. */
export function isStale(rule: PayloadRoute, c: RuleCoverage | undefined, nowMs: number): boolean {
  if (!isRuleActive(rule)) return false;
  const last = c?.lastReceivedAt ? Date.parse(c.lastReceivedAt) : NaN;
  return !Number.isFinite(last) || nowMs - last > STALE_AFTER_MS;
}
